import type { StatId } from '../domain/stats.ts';
import type { DamageType } from './damage.ts';

/**
 * The generic Effect / Modifier system.
 *
 * Everything that changes a crawler's numbers from outside their own Stats -
 * Buffs, Debuffs, worn equipment, and eventually Race and Class benefits - is
 * expressed as DATA in this shape and resolved here. Nothing gets a special
 * case in the engine.
 *
 * This module is deliberately content-free. It defines the shape and the
 * arithmetic; the Buffs, Debuffs and items that use it are a later slice.
 *
 * Sources for the modifier kinds represented: Rules for Survival p.42
 * (Damage Resistance, Elemental Resistance, Vulnerability, Immunity),
 * Rules p.11 (Enhanced Stats), C&C p.14 (Evade), C&C p.15 (Mana),
 * C&C p.16 (Move and Step), Rules p.14 (Advantage and Disadvantage sources),
 * C&C p.42 (Skill Ranks).
 */

/**
 * Where an Advantage or Disadvantage source applies.
 *
 * Only the scopes the supplied rules actually use are represented, and the
 * source is precise about which it means. Three DIFFERENT Stat-related
 * phrasings appear, and conflating them makes an Effect leak onto Checks it was
 * never printed to touch:
 *
 *  - "Roll with Advantage on Int and Con Stat Checks" (Frost Maiden, Floor3
 *    p.134) and "Advantage on all Stat Checks for a specific Stat" (Build
 *    System, p.159) -> 'stat-check': Stat Checks ONLY.
 *  - "you make Charisma-based Skill Checks with Disadvantage" (Cat, p.130) and
 *    "Roll with Advantage on all Skill Checks for a specific Stat" (p.160)
 *    -> 'stat-skill-check': Skill Checks using that Stat.
 *  - "make all Charisma-based Checks with Disadvantage" (Dwarf p.132, Elf
 *    p.133, Crocodilian p.131) -> 'stat-based-check': any Check using that Stat.
 *
 * Plus the non-Stat scopes: "Advantage on Cat-like Reflexes Skill Checks"
 * (skill), "Advantage on Evade Checks" (evade), "Mobs attack with
 * Disadvantage" (attack).
 *
 * This is NOT a general condition language. Add a scope when a supplied rule
 * needs one, and not before.
 */
export type CheckScope =
  | { kind: 'all' }
  | { kind: 'attack' }
  | { kind: 'evade' }
  /** Stat Checks using this Stat, and nothing else. */
  | { kind: 'stat-check'; stat: StatId }
  /** Skill Checks based on this Stat. */
  | { kind: 'stat-skill-check'; stat: StatId }
  /** Any Check that uses this Stat, whatever its kind. */
  | { kind: 'stat-based-check'; stat: StatId }
  | { kind: 'skill'; skillId: string };

/** What a Check being rolled actually is, for matching against a CheckScope. */
export interface CheckContext {
  kind: 'skill' | 'stat' | 'evade' | 'attack' | 'advancement';
  stat: StatId | null;
  skillId: string | null;
}

/** True when a scoped source applies to the Check being rolled. */
export function scopeApplies(scope: CheckScope, context: CheckContext): boolean {
  switch (scope.kind) {
    case 'all':
      return true;
    case 'attack':
      return context.kind === 'attack';
    case 'evade':
      return context.kind === 'evade';
    case 'stat-check':
      return context.kind === 'stat' && context.stat === scope.stat;
    case 'stat-skill-check':
      return context.kind === 'skill' && context.stat === scope.stat;
    case 'stat-based-check':
      return context.stat === scope.stat;
    case 'skill':
      return context.skillId === scope.skillId;
  }
}

/** What a modifier acts on. */
export type ModifierTarget =
  | { kind: 'stat'; stat: StatId }
  | { kind: 'skill-rank'; skillId: string }
  | { kind: 'damage-resistance' }
  | { kind: 'elemental-resistance'; damageType: DamageType }
  | { kind: 'vulnerability'; damageType: DamageType }
  | { kind: 'immunity'; damageType: DamageType }
  | { kind: 'evade' }
  | { kind: 'move' }
  | { kind: 'step' }
  | { kind: 'max-mana' }
  /**
   * A flat penalty or bonus applied to the Check total itself, scoped like an
   * Advantage source. Fatigued is "-1 penalty on all Checks" and the injuries
   * are -2 and -5 (Rules pp.45-46).
   */
  | { kind: 'check-modifier'; scope: CheckScope }
  | { kind: 'advantage'; scope: CheckScope }
  | { kind: 'disadvantage'; scope: CheckScope };

/**
 * `add` shifts a number. `grant` turns something on and needs no amount
 * (a resistance, an immunity, a source of Advantage).
 */
export interface Modifier {
  target: ModifierTarget;
  /**
   * `add` shifts a number, `grant` turns something on, `multiply` scales a
   * value. `multiply` exists because Fatigued halves Move (Rules p.45); it is
   * accepted on 'move' and 'step' only.
   */
  op: 'add' | 'grant' | 'multiply';
  amount?: number;
}

export type EffectSourceKind = 'buff' | 'debuff' | 'equipment' | 'race' | 'class' | 'skill';

export interface Effect {
  id: string;
  /** Stable content ID of whatever produced this effect. */
  sourceId: string;
  sourceKind: EffectSourceKind;
  /** Short label for the breakdown, e.g. "Hellfire Apron". */
  label: string;
  modifiers: Modifier[];
  /** False when an item is stowed or a Buff is not activated. */
  active?: boolean;
  /** False keeps this Buff or Debuff out of the public combat HUD. */
  visible?: boolean;
}

/** One line of a breakdown: which effect contributed what. */
export interface ModifierContribution {
  label: string;
  sourceId: string;
  amount: number;
}

export interface ResolvedModifiers {
  statBonuses: Record<StatId, number>;
  skillRankBonuses: Record<string, number>;
  damageResistance: number;
  elementalResistances: DamageType[];
  vulnerabilities: DamageType[];
  immunities: DamageType[];
  evade: number;
  move: number;
  step: number;
  /** Multiplied, not added. 0.5 halves Move. */
  moveMultiplier: number;
  stepMultiplier: number;
  /** Flat Check modifiers, kept scoped so they only apply where printed. */
  checkModifiers: { label: string; sourceId: string; amount: number; scope: CheckScope }[];
  maxMana: number;
  /**
   * Counted and SCOPED, not collapsed. Rules p.14 cancels sources one for one,
   * so each source is kept separately and matched against the Check being made.
   */
  advantageSources: { label: string; sourceId: string; scope: CheckScope }[];
  disadvantageSources: { label: string; sourceId: string; scope: CheckScope }[];
  /** Per-target audit trail, so every number on the sheet can be explained. */
  contributions: {
    stat: Record<StatId, ModifierContribution[]>;
    skillRank: Record<string, ModifierContribution[]>;
    damageResistance: ModifierContribution[];
    evade: ModifierContribution[];
  };
}

function emptyStatRecord(): Record<StatId, number> {
  return { str: 0, int: 0, con: 0, dex: 0, cha: 0 };
}

export function emptyResolvedModifiers(): ResolvedModifiers {
  return {
    statBonuses: emptyStatRecord(),
    skillRankBonuses: {},
    damageResistance: 0,
    elementalResistances: [],
    vulnerabilities: [],
    immunities: [],
    evade: 0,
    move: 0,
    step: 0,
    moveMultiplier: 1,
    stepMultiplier: 1,
    checkModifiers: [],
    maxMana: 0,
    advantageSources: [],
    disadvantageSources: [],
    contributions: {
      stat: { str: [], int: [], con: [], dex: [], cha: [] },
      skillRank: {},
      damageResistance: [],
      evade: [],
    },
  };
}

/**
 * Folds a list of effects into one set of numbers.
 *
 * Inactive effects are skipped. `add` modifiers accumulate; `grant` modifiers
 * are idempotent, so two sources of Fire Resistance give the same result as one
 * (Floor 3 source p.159: "two instances of Resistance to Slashing damage do
 * nothing more than one instance of it").
 */
export function resolveModifiers(effects: readonly Effect[]): ResolvedModifiers {
  const out = emptyResolvedModifiers();

  for (const effect of effects) {
    if (effect.active === false) continue;

    for (const modifier of effect.modifiers) {
      const amount = modifier.amount ?? 0;
      const contribution: ModifierContribution = {
        label: effect.label,
        sourceId: effect.sourceId,
        amount,
      };
      const target = modifier.target;

      switch (target.kind) {
        case 'stat':
          out.statBonuses[target.stat] += amount;
          out.contributions.stat[target.stat].push(contribution);
          break;
        case 'skill-rank': {
          out.skillRankBonuses[target.skillId] =
            (out.skillRankBonuses[target.skillId] ?? 0) + amount;
          const list = out.contributions.skillRank[target.skillId] ?? [];
          list.push(contribution);
          out.contributions.skillRank[target.skillId] = list;
          break;
        }
        case 'damage-resistance':
          out.damageResistance += amount;
          out.contributions.damageResistance.push(contribution);
          break;
        case 'elemental-resistance':
          addOnce(out.elementalResistances, target.damageType);
          break;
        case 'vulnerability':
          addOnce(out.vulnerabilities, target.damageType);
          break;
        case 'immunity':
          addOnce(out.immunities, target.damageType);
          break;
        case 'evade':
          out.evade += amount;
          out.contributions.evade.push(contribution);
          break;
        case 'move':
          if (modifier.op === 'multiply') out.moveMultiplier *= amount;
          else out.move += amount;
          break;
        case 'step':
          if (modifier.op === 'multiply') out.stepMultiplier *= amount;
          else out.step += amount;
          break;
        case 'check-modifier':
          out.checkModifiers.push({
            label: effect.label,
            sourceId: effect.sourceId,
            amount,
            scope: target.scope,
          });
          break;
        case 'max-mana':
          out.maxMana += amount;
          break;
        case 'advantage':
          out.advantageSources.push({
            label: effect.label,
            sourceId: effect.sourceId,
            scope: target.scope,
          });
          break;
        case 'disadvantage':
          out.disadvantageSources.push({
            label: effect.label,
            sourceId: effect.sourceId,
            scope: target.scope,
          });
          break;
      }
    }
  }

  return out;
}

function addOnce<T>(list: T[], value: T): void {
  if (!list.includes(value)) list.push(value);
}

/** Convenience for the Enhanced Stat layer, which wants labelled sources. */
export function statModifierSources(
  resolved: ResolvedModifiers,
): { label: string; stat: StatId; amount: number }[] {
  const sources: { label: string; stat: StatId; amount: number }[] = [];
  for (const stat of ['str', 'int', 'con', 'dex', 'cha'] as StatId[]) {
    for (const contribution of resolved.contributions.stat[stat]) {
      sources.push({ label: contribution.label, stat, amount: contribution.amount });
    }
  }
  return sources;
}

/**
 * How many Advantage sources apply to a particular Check.
 * Feeds `resolveRollMode` in rules/rollModes.ts; there is no second
 * Advantage/Disadvantage algorithm anywhere.
 */
export function advantageSourcesFor(
  resolved: ResolvedModifiers,
  context: CheckContext,
): number {
  return resolved.advantageSources.filter((s) => scopeApplies(s.scope, context)).length;
}

export function disadvantageSourcesFor(
  resolved: ResolvedModifiers,
  context: CheckContext,
): number {
  return resolved.disadvantageSources.filter((s) => scopeApplies(s.scope, context)).length;
}

/** The flat Check modifiers that apply to the Check being made. */
export function checkModifiersFor(
  resolved: ResolvedModifiers,
  context: CheckContext,
): { label: string; sourceId: string; amount: number }[] {
  return resolved.checkModifiers
    .filter((m) => scopeApplies(m.scope, context))
    .map(({ label, sourceId, amount }) => ({ label, sourceId, amount }));
}
